/**
 * Custom hook for flow validation and export operations
 * Handles validation, JSON generation, and export functionality
 */

import { useState } from 'react';
import { flowHelpers } from '../services/whatsappBusinessAPI';

export const useFlowValidation = (validateFlow, generateFlowJson) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const hideSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const validateAndExportJson = async () => {
    const validation = validateFlow();
    
    if (validation.errors.length > 0) {
      showSnackbar(`Cannot export: ${validation.errors[0]}`, 'error');
      return null;
    }

    if (validation.warnings.length > 0) {
      showSnackbar(`Warning: ${validation.warnings[0]} Flow may not work correctly.`, 'warning');
    }

    return await generateFlowJson();
  };

  const validateForDeployment = async (deployToWhatsApp, flowName, isConnected, selectedWABA) => {
    if (!isConnected || !selectedWABA) {
      showSnackbar('Please connect your WhatsApp Business account first.', 'warning');
      return false;
    }

    try {
      const validation = validateFlow();
      
      if (validation.errors.length > 0) {
        showSnackbar(`Cannot deploy: ${validation.errors[0]}`, 'error');
        return false;
      }

      const flowJson = await generateFlowJson();
      const flowValidation = flowHelpers.validateBeforeDeployment(flowJson);
      
      if (!flowValidation.isValid) {
        showSnackbar(`Deployment validation failed: ${flowValidation.errors[0]}`, 'error');
        return false;
      }

      // Show warnings but allow deployment
      if (flowValidation.warnings.length > 0) {
        showSnackbar(`Warning: ${flowValidation.warnings[0]} Deploying anyway...`, 'warning');
      }

      await deployToWhatsApp(flowJson, flowName);
      
      showSnackbar(`Flow "${flowName}" deployed successfully to WhatsApp Business!`, 'success');
      return true;

    } catch (error) {
      console.error('Deployment failed:', error);
      showSnackbar(`Deployment failed: ${error.message}`, 'error');
      return false;
    }
  };

  const validateForSave = (flowData, isUpdate = false) => {
    const validation = validateFlow();
    let severity = 'success';
    let message = `Flow "${flowData.name}" ${isUpdate ? 'updated' : 'saved'} successfully!`;

    if (validation.warnings.length > 0) {
      message += ` Note: ${validation.warnings.length} warning(s) detected.`;
      severity = 'warning';
    }

    showSnackbar(message, severity);
    return { isValid: true, validation };
  };

  // Combined save and deploy validation
  const validateForSaveAndDeploy = async (flowData, isUpdate = false, isConnected = false, selectedWABA = null, deployToWhatsApp = null) => {
    try {
      // First validate for save
      const saveValidation = validateForSave(flowData, isUpdate);

      // If connected to WhatsApp, also deploy
      if (isConnected && selectedWABA && deployToWhatsApp) {
        console.log('Auto-deploying to WhatsApp after save...');

        // Validate for deployment
        const flowJson = await generateFlowJson();
        const flowValidation = flowHelpers.validateBeforeDeployment(flowJson);

        if (!flowValidation.isValid) {
          showSnackbar(`Flow saved but deployment failed: ${flowValidation.errors[0]}`, 'warning');
          return { isValid: true, deployed: false, validation: saveValidation.validation };
        }

        // Deploy to WhatsApp
        const deployResult = await deployToWhatsApp(flowJson, flowData.name, flowData.whatsappFlowId);

        // Update success message to include deployment
        let message = `Flow "${flowData.name}" ${isUpdate ? 'updated' : 'saved'} and deployed to WhatsApp successfully!`;
        let severity = 'success';

        if (flowValidation.warnings.length > 0) {
          message += ` Note: ${flowValidation.warnings.length} warning(s) detected.`;
          severity = 'warning';
        }

        showSnackbar(message, severity);
        return {
          isValid: true,
          deployed: true,
          validation: saveValidation.validation,
          whatsappFlowId: deployResult?.id // Return the WhatsApp Flow ID
        };
      }

      return { isValid: true, deployed: false, validation: saveValidation.validation };
    } catch (error) {
      console.error('Save and deploy failed:', error);
      showSnackbar(`Flow saved but deployment failed: ${error.message}`, 'warning');
      return { isValid: true, deployed: false, error: error.message };
    }
  };

  return {
    snackbar,
    showSnackbar,
    hideSnackbar,
    validateAndExportJson,
    validateForDeployment,
    validateForSave,
    validateForSaveAndDeploy,
  };
};